import queryString from 'query-string';
import {useEffect} from 'react';

import {Entity, EntityQuery, Member} from '../../generated/graphql';
import {TeamBaseEntity} from '../../graphql/teamQuery';
import {Viewer} from '../../state/viewer/types';
import {viewerUsingAdminPrivileges} from '../../util/admin';
import {setAuthRedirect, setIsFollowingInviteLink} from '../../util/auth';
import {safeLocalStorage} from '../../util/localStorage';
import {forceRedirect} from '../../util/navigation';

/**
 * Note: this isn't technically a hook, it's just a utility function
 *
 */
export const checkIfTeamMemberOrAdmin = (
  viewer: Viewer | undefined,
  teamMembers: Partial<Member[]>
) => {
  if (!teamMembers || !viewer) {
    return false;
  }

  const viewerHasAdminCookie = viewerUsingAdminPrivileges();
  if (viewer.admin && viewerHasAdminCookie) {
    return true;
  }

  return teamMembers.some(member => member?.id === viewer.id);
};

export const useViewerMember = (
  entity: TeamBaseEntity | Entity | EntityQuery['entity'],
  viewer?: Viewer
) => {
  if (!entity || !viewer) {
    return undefined;
  }

  // https://github.com/microsoft/TypeScript/issues/44373
  const members = (entity?.members ?? []) as any[];
  return members.find(m => m?.username === viewer.username) as Member;
};

type UseRedirectToSignupIfFollowingInviteLinkAndNotLoggedInProps = {
  isInviteToNewUser: boolean;
  isViewerLoggedIn: boolean;
};

export const useRedirectToSignupIfFollowingInviteLinkAndNotLoggedIn = ({
  isInviteToNewUser,
  isViewerLoggedIn,
}: UseRedirectToSignupIfFollowingInviteLinkAndNotLoggedInProps) => {
  // Redirect to signup if user is not logged in and is following an invite link
  useEffect(() => {
    if (isInviteToNewUser && !isViewerLoggedIn) {
      // Invitation is to a new user, redirect them to signup and then back to team page
      setIsFollowingInviteLink(true);
      setAuthRedirect(
        document.location.href +
          document.location.href.includes('&newUser=true')
          ? ''
          : '&newUser=true'
      );

      // force redirect with appropriate accomodations for analytics and timing
      forceRedirect('/login?signup=true');
    }
  }, [isInviteToNewUser, isViewerLoggedIn]);
};

export const isFollowingInviteLinkAndToNewUser = (
  entityName: string,
  callingComponentName: string
) => {
  const {
    invited: invitedParam,
    newUser: newUserParam,
  }: queryString.ParsedQuery<string> = queryString.parse(
    document.location.search
  );

  let isFollowingInviteLink = false;
  let inviteIsToNewUser = false;
  if (invitedParam != null && invitedParam.length > 0) {
    const invited: string =
      typeof invitedParam === 'string' ? invitedParam : invitedParam[0];
    safeLocalStorage.setItem('auth.invited', invited);
    isFollowingInviteLink = true;
    window.analytics?.track('Invite Accepted', {
      entityName,
      location: callingComponentName,
    });
  }
  if (newUserParam && newUserParam.length > 0) {
    inviteIsToNewUser =
      typeof newUserParam === 'string'
        ? newUserParam === 'true'
        : newUserParam[0] === 'true';
  }
  return {
    isFollowingInviteLink,
    inviteIsToNewUser,
  };
};
